import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";
import { defaultWMS1021_R01DetailDto, defaultWMS1021_R01HeaderDto, WMS1021_R01DetailDto, WMS1021_R01HeaderDto } from './Dto_WMS1021_R01';
import { showWarning } from 'src/utils/toast/toastUtil';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';


export interface WMS1021_R01Filter {
	dateRange: DateRange | undefined;
	wmsOrderNo: string;
	customCd: string;
	customNm: string;
	/** 저장 시 품목 라벨 자동 출력 (Y/N) */
	labelPrintAuto: string;
}

export interface Store_WMS1021_R01 {
	WMS1021_R01Filter: WMS1021_R01Filter;
	WMS1021_R01PastFilter: WMS1021_R01Filter;
	setWMS1021_R01Filter: (filter: Partial<Store_WMS1021_R01['WMS1021_R01Filter']>) => void;
	setWMS1021_R01PastFilter: (filter: Store_WMS1021_R01['WMS1021_R01PastFilter']) => void;

	WMS1021_R01List: WMS1021_R01HeaderDto[];
	setWMS1021_R01List: (dataList: WMS1021_R01HeaderDto[]) => void;

	editWMS1021_R01: WMS1021_R01HeaderDto | null;
	setEditWMS1021_R01: (editSale: WMS1021_R01HeaderDto | null) => void;

	selectedWMS1021_R01: WMS1021_R01HeaderDto | undefined;
	setSelectedWMS1021_R01: (data: WMS1021_R01HeaderDto | undefined) => void;
	initSelectedWMS1021_R01: () => void;

	deletedWMS1021_R01DetailList: string[];
	setDeletedWMS1021_R01DetailList: (idList: string[]) => void;
	addDeletedWMS1021_R01DetailList: (idList: string[]) => void;
	removeDeletedWMS1021_R01DetailList: (idList?: string[]) => void;

	WMS1021_R01DetailList: WMS1021_R01DetailDto[];
	setWMS1021_R01DetailList: (data: WMS1021_R01DetailDto[]) => void;
	insertWMS1021_R01DetailList: (data: WMS1021_R01DetailDto[], index?: number) => void;
	removeWMS1021_R01DetailList: (index: number) => void;
	updateWMS1021_R01DetailList: (index: number, data: WMS1021_R01DetailDto) => void;

	selectedWMS1021_R01DetailList: string[];
	setSelectedWMS1021_R01DetailList: (idList: string[]) => void;
	addSelectedWMS1021_R01DetailList: (idList: string[]) => void;
	removeSelectedWMS1021_R01DetailList: (idList?: string[]) => void;
	initSelectedWMS1021_R01DetailList: () => void;

	originalWMS1021_R01DetailList: WMS1021_R01DetailDto[];
	setOriginalWMS1021_R01DetailList: (data: WMS1021_R01DetailDto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	transportComplete: boolean;
	setTransportComplete: (transportComplete: boolean) => void;
	detailFetchTrigger: boolean;
	setDetailFetchTrigger: (detailFetchTrigger: boolean) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	initData: () => void;
	warehouseCdListToUseList: (logicCenter: string) => WareHouseDto[];
	syncInitialData: () => void;
	/** 입고합계가 0일 때 주문잔량으로 자동 설정 */
	setAutoTransportQtyIfEmpty: (detailRow: WMS1021_R01DetailDto) => void;
}

const clampUpdateTransportQty = (data: WMS1021_R01DetailDto): WMS1021_R01DetailDto => {
	let qty = Number(data.updateTransportQty) || 0;

	if (qty < 0) {
		showWarning('입고량은 0보다 작을 수 없습니다.');
		qty = 0;
	}

	const maxQty = Number(data.orderQtyView) || 0;
	if (qty > maxQty) {
		showWarning(`입고량이 주문잔량(${maxQty})보다 클 수 없습니다. - 품목코드: ${data.itemCd}`);
		qty = maxQty;
	}

	return { ...data, updateTransportQty: qty };
};

export const defaultWMS1021_R01Filter: WMS1021_R01Filter = {
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	wmsOrderNo: '',
	customCd: '',
	customNm: '',
	labelPrintAuto: 'N',
};

const getInitialWMS1021_R01HeaderDto = (): WMS1021_R01HeaderDto => {
	const { wmsCdList, warehouseCdList } = useCommonCodeStore.getState();
	return {
		...defaultWMS1021_R01HeaderDto,
		logicCenter: wmsCdList?.[0]?.minorCd || '',
		warehouseCd: warehouseCdList?.[0]?.warehouseCd || '',
		warehouseNm: warehouseCdList?.[0]?.warehouseNm || '',
	};
};

export const useWMS1021_R01Store = create<Store_WMS1021_R01>((set, get) => ({
	WMS1021_R01Filter: defaultWMS1021_R01Filter,
	WMS1021_R01PastFilter: defaultWMS1021_R01Filter,
	setWMS1021_R01Filter: (update) => set((state) => ({
		WMS1021_R01Filter: {
			...state.WMS1021_R01Filter,
			...update
		}
	})),
	setWMS1021_R01PastFilter: (filter) => set((state) => ({
		...state,
		WMS1021_R01PastFilter: filter
	})),

	WMS1021_R01List: [],
	setWMS1021_R01List: (dataList) => set({ WMS1021_R01List: dataList }),

	editWMS1021_R01: getInitialWMS1021_R01HeaderDto(),
	setEditWMS1021_R01: (editSale) => set({ editWMS1021_R01: editSale }),

	selectedWMS1021_R01: undefined,
	setSelectedWMS1021_R01: (data) => set({
		selectedWMS1021_R01: data,
		selectedWMS1021_R01DetailList: [],
		deletedWMS1021_R01DetailList: [],
		detailFetchTrigger: true,
		detailScrollTop: null,
	}),
	initSelectedWMS1021_R01: () => {
		set({
			selectedWMS1021_R01: undefined,
			editWMS1021_R01: getInitialWMS1021_R01HeaderDto(),
			WMS1021_R01DetailList: [],
			detailFetchTrigger: false,
		});
	},

	WMS1021_R01DetailList: [],
	setWMS1021_R01DetailList: (data) => {
		let newList: WMS1021_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		set({ WMS1021_R01DetailList: newList });
	},
	insertWMS1021_R01DetailList: (data, index) => set((state) => {
		let newWMS1021_R01DetailList = [...state.WMS1021_R01DetailList];
		const targetRowData = newWMS1021_R01DetailList.find(item => item.valueIndex === index);
		const targetOrderIndex = targetRowData?.orderIndex || 0;
		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		let maxValueIndex = Math.max(0, ...newWMS1021_R01DetailList.map(item => item.valueIndex || 0)) + 1;

		const targetRows = newWMS1021_R01DetailList.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if (item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		newWMS1021_R01DetailList = newWMS1021_R01DetailList.filter(item => item.orderIndex !== targetOrderIndex);
		newWMS1021_R01DetailList.push(...targetRows);

		const newData = data.map(item => ({
			...defaultWMS1021_R01DetailDto,
			...item,
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));

		if (index !== undefined) {
			newWMS1021_R01DetailList.splice(index, 0, ...newData);
		} else {
			newWMS1021_R01DetailList.push(...newData);
		}

		return { ...state, WMS1021_R01DetailList: newWMS1021_R01DetailList };
	}),
	removeWMS1021_R01DetailList: (index) => set((state) => ({
		...state,
		WMS1021_R01DetailList: state.WMS1021_R01DetailList.filter(item => item.valueIndex !== index)
	})),
	updateWMS1021_R01DetailList: (valueIndex, data) => set((state) => ({
		...state,
		WMS1021_R01DetailList: state.WMS1021_R01DetailList.map(item =>
			item.valueIndex === valueIndex ? clampUpdateTransportQty(data) : item
		)
	})),

	deletedWMS1021_R01DetailList: [],
	setDeletedWMS1021_R01DetailList: (idList) => set({ deletedWMS1021_R01DetailList: idList }),
	addDeletedWMS1021_R01DetailList: (idList) => set((state) => {
		const _deletedDetailList = [...state.deletedWMS1021_R01DetailList];
		for (const valueIndex of idList) {
			const isInserted = state.WMS1021_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.WMS1021_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if (!isInserted && !isModified) {
				_deletedDetailList.push(valueIndex);
			}
		}
		return { ...state, deletedWMS1021_R01DetailList: _deletedDetailList };
	}),
	removeDeletedWMS1021_R01DetailList: (idList) => set((state) => ({
		...state,
		deletedWMS1021_R01DetailList: state.deletedWMS1021_R01DetailList.filter(value => idList ? !idList.includes(value) : false)
	})),

	selectedWMS1021_R01DetailList: [],
	setSelectedWMS1021_R01DetailList: (idList) => set({
		selectedWMS1021_R01DetailList: idList,
		detailScrollTop: null,
	}),
	addSelectedWMS1021_R01DetailList: (idList) => set((state) => ({
		...state,
		selectedWMS1021_R01DetailList: [...state.selectedWMS1021_R01DetailList, ...idList]
	})),
	removeSelectedWMS1021_R01DetailList: (idList) => set((state) => ({
		...state,
		selectedWMS1021_R01DetailList: state.selectedWMS1021_R01DetailList.filter(value => idList ? !idList.includes(value) : false)
	})),
	initSelectedWMS1021_R01DetailList: () => set({ selectedWMS1021_R01DetailList: [] }),

	originalWMS1021_R01DetailList: [],
	setOriginalWMS1021_R01DetailList: (data) => {
		let newList: WMS1021_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...defaultWMS1021_R01DetailDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		set({ originalWMS1021_R01DetailList: newList });
	},

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),

	detailColumnState: null,
	setDetailColumnState: (state) => set({ detailColumnState: state }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	transportComplete: false,
	setTransportComplete: (transportComplete) => set({ transportComplete }),
	detailFetchTrigger: false,
	setDetailFetchTrigger: (detailFetchTrigger) => set({ detailFetchTrigger }),

	scrollTop: null,
	setScrollTop: (top) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top) => set({ detailScrollTop: top }),

	initData: () => {
		set({
			WMS1021_R01Filter: defaultWMS1021_R01Filter,
			WMS1021_R01PastFilter: defaultWMS1021_R01Filter,
			WMS1021_R01List: [],
			selectedWMS1021_R01: undefined,
			WMS1021_R01DetailList: [],
			deletedWMS1021_R01DetailList: [],
			selectedWMS1021_R01DetailList: [],
			originalWMS1021_R01DetailList: [],
			editWMS1021_R01: getInitialWMS1021_R01HeaderDto(),
			allCount: 0,
			transportComplete: false,
			detailFetchTrigger: false,
			scrollTop: null,
			detailScrollTop: null,
		});
	},

	warehouseCdListToUseList: (logicCenter) => {
		const { warehouseCdList } = useCommonCodeStore.getState();
		const filteredList = warehouseCdList.filter(value =>
			value.logicCenter === logicCenter && value.materialYn === 'Y'
		);

		if (filteredList.length === 0) {
			return [{ warehouseCd: '', warehouseNm: '' } as WareHouseDto];
		}

		return filteredList;
	},

	syncInitialData: () => {
		const { wmsCdList } = useCommonCodeStore.getState();
		const state = get();

		if ((state.editWMS1021_R01?.logicCenter && state.editWMS1021_R01?.warehouseCd) || wmsCdList.length === 0) return;

		const defaultLogicCenter = wmsCdList?.[0]?.minorCd || '';
		const availableWarehouses = state.warehouseCdListToUseList(defaultLogicCenter);
		const defaultWarehouseCd = availableWarehouses?.[0]?.warehouseCd || '';
		const defaultWarehouseNm = availableWarehouses?.[0]?.warehouseNm || '';

		set({
			editWMS1021_R01: {
				...state.editWMS1021_R01!,
				logicCenter: defaultLogicCenter,
				warehouseCd: defaultWarehouseCd,
				warehouseNm: defaultWarehouseNm,
			}
		});
	},

	setAutoTransportQtyIfEmpty: (detailRow) => {
		if (detailRow.updateTransportQty !== 0) return;
		if (!detailRow.orderQtyView || detailRow.orderQtyView <= 0) return;

		const { WMS1021_R01DetailList } = get();
		const updated = clampUpdateTransportQty({
			...detailRow,
			updateTransportQty: detailRow.orderQtyView,
			isModified: true,
		});

		set({
			WMS1021_R01DetailList: WMS1021_R01DetailList.map(item =>
				item.valueIndex === detailRow.valueIndex ? updated : item
			),
		});
	},
}));
